<template>
  <div style="margin-top: -20px;">
    <v-container fluid class="group-page">
      <!-- Header -->
      <div class="header-row">
        <v-btn
          @click="$router.go(-1)"
          elevation="0"
          rounded
          large
          class="font-made modern-back-btn px-6 py-3 d-flex align-center"
        >
          <v-icon left size="22" class="back-icon">mdi-arrow-left</v-icon>
          <span class="font-weight-bold text-uppercase" style="letter-spacing:1px; font-size:14px;">
            {{ $t("Back") }}
          </span>
        </v-btn>

        <div class="header-info">
          <h1 class="title">
            {{$t("Group")}}: <span class="title-accent">{{ group.code }}</span>
          </h1>
          <div class="subtitle">
            {{ group.educationFaculty.name }} •
            {{ group.educationType.name }} •
            {{ group.educationLanguage.name }}
          </div>
        </div>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="loader">
        {{ $t('Loading group details') }}…
      </div>

      <!-- Stats Card + Year Filter -->
      <div v-else class="stats-wrapper">
        <div class="stats-card">
          <div class="stat-item">
            <div class="stat-label">{{$t("Total Students")}}</div>
            <div class="stat-value">
              {{ displayStats.total }}
            </div>
          </div>
          <div class="stat-item">
            <div class="stat-label">{{$t("Male Students")}}</div>
            <div class="stat-value">
              {{ displayStats.male }}
            </div>
          </div>
          <div class="stat-item">
            <div class="stat-label">{{$t("Female Students")}}</div>
            <div class="stat-value">
              {{ displayStats.female }}
            </div>
          </div>
        </div>
        <div class="year-filter-top">
          <div>
            <v-select
              v-model="selectedYear"
              :items="yearOptions"
              :placeholder="$t('All Years')"
              dense
              outlined
              hide-details
              clearable
              class="year-select-top"
              :menu-props="{ bottom: true, offsetY: true }"
              @change="onYearChange"
            />
            <div class="year-current-label">
              {{ selectedYear || $t('All Years') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Teacher Linking -->
      <div class="teacher-card" v-if="!loading">
        <div class="teacher-card-inner">
          <div class="teacher-label">{{ $t("Teacher") }}</div>

          <!-- Display mode -->
          <div v-if="group.teacherName && group.teacherName.trim() && !editingTeacher" class="teacher-display">
            <div class="teacher-info">
              <v-icon v-if="isTeacherFemale" size="20" color="#d81b60" class="mr-2">mdi-human-female</v-icon>
              <v-icon v-else size="20" color="#1565c0" class="mr-2">mdi-human-male</v-icon>
              <span class="teacher-name">{{ group.teacherName }}</span>
            </div>
            <button v-if="canUpdate" class="teacher-edit-btn" @click="editingTeacher = true">
              <v-icon small>mdi-pencil-outline</v-icon>
            </button>
          </div>

          <!-- Edit mode / No teacher -->
          <div v-else class="teacher-row">
            <v-autocomplete
              v-model="selectedTeacherId"
              :items="teachers"
              item-text="name"
              item-value="id"
              :placeholder="$t('Select teacher')"
              dense
              outlined
              hide-details
              clearable
              class="teacher-select"
              :loading="teacherLoading"
              :menu-props="{ bottom: true, offsetY: true, maxHeight: 250 }"
              @change="onTeacherChange"
            />
            <button v-if="group.teacherName && group.teacherName.trim()" class="teacher-cancel-btn" @click="editingTeacher = false">
              <v-icon small>mdi-close</v-icon>
            </button>
          </div>
        </div>
      </div>

      <!-- Students Table -->
      <div class="students-table">
        <v-card class="students-card" elevation="2">
          <v-data-table
            :headers="studentHeaders"
            :items="sortedStudents"
            :search="studentSearch"
            :options.sync="studentOptions"
            :server-items-length="studentTotal"
            :loading="studentLoading"
            class="power-table"
            hide-default-footer
            :item-class="studentRowClass"
            @click:row="onRowClick"
          >
            <template #item.rowIndex="{ item, index }">
              <span v-if="getStudentRank(item) <= 5" class="s-rank-badge">{{ index + 1 }}</span>
              <span v-else class="s-row-num">{{ index + 1 }}</span>
            </template>
            <template #item.totalSumma="{ item }">
              <span class="text-no-wrap">{{ formatMoney(item.totalSumma) }}</span>
            </template>
            <template #item.totalPaidSumma="{ item }">
              <span class="text-no-wrap" style="color:#2e7d32">{{ formatMoney(item.totalPaidSumma) }}</span>
            </template>
            <template #header.paidPercent="{ header }">
              <span style="cursor:pointer;user-select:none" @click="togglePctSort">
                {{ header.text }}
                <v-icon x-small class="ml-1">{{ pctSortDir === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
              </span>
            </template>
            <template #item.paidPercent="{ item }">
              <div class="d-flex align-center" style="min-width:120px">
                <div class="s-pct-bar mr-2">
                  <div
                    class="s-pct-fill"
                    :class="getStudentRank(item) <= 5 ? 's-pct-gold' : sPctColor(item.paidPercent || 0)"
                    :style="{width: Math.min(item.paidPercent || 0, 100)+'%'}"
                  />
                </div>
                <span
                  class="caption font-weight-bold"
                  :class="getStudentRank(item) <= 5 ? 's-pct-text-gold' : sPctTextColor(item.paidPercent || 0)"
                >{{ item.paidPercent || 0 }}%</span>
              </div>
            </template>
            <template #item.contractYear="{ item }">
              <v-chip
                :color="yearColor(item.contractYear)"
                dark
                small
                label
              >
                {{ item.contractYear }}
              </v-chip>
            </template>
            <template #item.paidStatus="{ item }">
              <v-chip
                :color="item.paidStatus === 'PAID' ? 'green lighten-1' : 'red lighten-1'"
                dark
                small
              >
                {{ item.paidStatus }}
              </v-chip>
            </template>

            <template #progress>
              <table-skeleton :cols="studentHeaders.length" />
            </template>
            <template #no-data>
              <empty-state :title="$t('No results found')" />
            </template>
            <template #footer.page-text>
              {{ studentOptions.page }} / {{ Math.ceil(studentTotal / studentOptions.itemsPerPage) }}
            </template>
            <template #footer>
              <v-pagination
                v-model="studentOptions.page"
                :length="Math.ceil(studentTotal / studentOptions.itemsPerPage)"
                circle
              />
            </template>
          </v-data-table>
        </v-card>
      </div>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios';
import localAxios from '@/utils/localAxios';
import EmptyState from '@/components/EmptyState.vue';
import TableSkeleton from '@/components/TableSkeleton.vue';
import { generateYearOptions, getStoredYear, setStoredYear, toFullYear } from '@/service/year-options';

export default {
  name: 'GroupDetailPage',
  components: { EmptyState, TableSkeleton },
  data() {
    return {
      group: {},
      loading: false,
      canUpdate: false,
      teachers: [],
      selectedTeacherId: null,
      teacherLoading: false,
      editingTeacher: false,

      // Students table state
      selectedYear: toFullYear(getStoredYear()) || null,
      yearOptions: generateYearOptions('full'),
      pctSortDir: 'desc',
      studentHeaders: [
        { text: '#', value: 'rowIndex', sortable: false, width: '50px' },
        { text: this.$t('First name'), value: 'firstName', sortable: true },
        { text: this.$t('Last name'),  value: 'lastName',  sortable: true },
        { text: this.$t('Phone'),      value: 'mobilePhone' },
        { text: this.$t('Passport'),   value: 'passportNumber' },
        { text: this.$t('Contract Number'), value: 'contractNumber' },
        { text: this.$t('Contract sum'), value: 'totalSumma', sortable: false },
        { text: this.$t('Paid'), value: 'totalPaidSumma', sortable: false },
        { text: this.$t('Payment') + ' %', value: 'paidPercent', sortable: false },
        { text: this.$t('Year'),       value: 'contractYear' },
        { text: this.$t('Status'),     value: 'paidStatus' },
        { text: this.$t('Course'),       value: 'kurs' },
      ],
      students: [],
      studentTotal: 0,
      uniqueStats: null,
      studentLoading: false,
      studentOptions: {
        page: 1,
        itemsPerPage: 100,
        sortBy: [],
        sortDesc: [],
      },
      studentSearch: '',
    };
  },
  computed: {
    isTeacherFemale() {
      const name = (this.group.teacherName || '').trim()
      if (!name) return false
      const surname = name.split(/\s+/)[0].toLowerCase()
      return surname.endsWith('ova') || surname.endsWith('eva') || surname.endsWith('va')
    },
    sortedStudents() {
      return [...this.students].sort((a, b) => {
        const ap = a.paidPercent || 0
        const bp = b.paidPercent || 0
        return this.pctSortDir === 'asc' ? ap - bp : bp - ap
      })
    },
    displayStats() {
      if (!this.selectedYear) {
        if (this.uniqueStats) return this.uniqueStats;
        return {
          total: this.group.countOfStudentsInGroup ?? 0,
          male: this.group.countOfMaleStudentsInGroup ?? 0,
          female: this.group.countOfFemaleStudentsInGroup ?? 0,
        };
      }
      const total = this.studentTotal;
      const male = this.students.filter(s => s.gender === 'MALE').length;
      const female = this.students.filter(s => s.gender === 'FEMALE').length;
      return { total, male, female };
    },
  },
  async mounted() {
    this.canUpdate = await this.hasRole("GURUHLAR", "UPDATE");
    this.loading = true;
    try {
      const { data } = await axios.get(`/api/groups/${this.$route.params.id}`);
      this.group = data;
      this.selectedTeacherId = data.teacherId || null;
      await this.fetchTeachers();
      await this.fetchStudents();
      this.fetchUniqueStats();
    } catch (err) {
      console.error('Failed to fetch group or students:', err);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchTeachers() {
      this.teacherLoading = true;
      try {
        const { data } = await axios.get('/admin/teacher', {
          params: { page: 0, size: 1000, name: '', sortField: 'name', sortOrder: 'asc' }
        });
        this.teachers = data.content || [];
      } catch (err) {
        console.error('Failed to fetch teachers:', err);
      } finally {
        this.teacherLoading = false;
      }
    },

    async linkTeacher() {
      try {
        await axios.put(`/api/groups/${this.$route.params.id}/teacher`, null, {
          params: { teacherId: this.selectedTeacherId || '' }
        });
        const { data } = await axios.get(`/api/groups/${this.$route.params.id}`);
        this.group = data;
      } catch (err) {
        console.error('Failed to link teacher:', err);
      }
    },
    async onTeacherChange() {
      await this.linkTeacher()
      this.editingTeacher = false
    },

    onYearChange() {
      setStoredYear(this.selectedYear);
      this.studentOptions.page = 1;
      this.fetchStudents();
    },

    yearColor(year) {
      const colors = {
        '2025-2026': '#6200ee',
        '2024-2025': '#1976d2',
        '2023-2024': '#388e3c',
        '2022-2023': '#f57c00',
      };
      return colors[year] || '#757575';
    },

    async fetchStudents() {
      this.studentLoading = true;
      try {
        const { data } = await axios.get(
          `/api/groups/${this.$route.params.id}/students`,
          {
            params: {
              page: this.studentOptions.page - 1,
              size: this.studentOptions.itemsPerPage,
              year: this.selectedYear || undefined,
            },
          }
        );
        this.students = data.content;
        this.studentTotal = data.totalElements;
      } catch (err) {
        console.error('Failed to fetch students:', err);
      } finally {
        this.studentLoading = false;
      }
    },

    async fetchUniqueStats() {
      try {
        const { data } = await axios.get(
          `/api/groups/${this.$route.params.id}/students`,
          { params: { page: 0, size: 10000 } }
        );
        const all = data.content || [];
        const seen = new Set();
        const unique = [];
        for (const s of all) {
          const key = s.pinflNumber || s.id;
          if (!seen.has(key)) {
            seen.add(key);
            unique.push(s);
          }
        }
        this.uniqueStats = {
          total: unique.length,
          male: unique.filter(s => s.gender === 'MALE').length,
          female: unique.filter(s => s.gender === 'FEMALE').length,
        };
      } catch (err) {
        console.error('Failed to fetch unique stats:', err);
      }
    },

    // async onRowClick(item) {
    //   console.log("group info", this.group);
    //   console.log('Row clicked:', item);

    //   // Fetch contract data for the student and show in console
    //   try {
    //     const { data } = await axios.get(`admin/contract2025/open/${item.id}`);
    //     console.log('Contract data:', data);
    //     window.location.replace(`/contract2025-show/${item.id}`);
    //     return;
    //   } catch (err) {
    //     // console.error('Failed to fetch contract data:', err);
    //   }

    //   try {
    //     const { data } = await axios.get(`admin/contract2024/open/${item.id}`);
    //     console.log('Contract data:', data);
    //     window.location.replace(`/contract2024-show/${item.id}`);
    //     return;
    //   } catch (err) {
    //     // console.error('Failed to fetch contract data:', err);
    //   }

    //   try {
    //     const { data } = await axios.get(`admin/contract2023/open/${item.id}`);
    //     console.log('Contract data:', data);
    //     window.location.replace(`/contract2023-show/${item.id}`);
    //     return;
    //   } catch (err) {
    //     // console.error('Failed to fetch contract data:', err);
    //   }

    //   try {
    //     const { data } = await axios.get(`admin/contract2022/open/${item.id}`);
    //     console.log('Contract data:', data);
    //     window.location.replace(`/contract-show/${item.id}`);
    //     return;
    //   } catch (err) {
    //     // console.error('Failed to fetch contract data:', err);
    //   }
    // }

    formatMoney(val) {
      if (!val) return '0'
      return Number(val).toLocaleString('uz-UZ')
    },
    togglePctSort() {
      this.pctSortDir = this.pctSortDir === 'desc' ? 'asc' : 'desc'
    },
    getStudentRank(item) {
      const sorted = [...this.students].sort((a, b) => (b.paidPercent || 0) - (a.paidPercent || 0))
      return sorted.findIndex(i => i.id === item.id) + 1
    },
    studentRowClass(item) {
      return this.getStudentRank(item) <= 5 ? 's-gold-row' : ''
    },
    sPctColor(pct) {
      if (pct >= 80) return 's-pct-green'
      if (pct >= 50) return 's-pct-yellow'
      return 's-pct-red'
    },
    sPctTextColor(pct) {
      if (pct >= 80) return 'green--text text--darken-2'
      if (pct >= 50) return 'orange--text text--darken-2'
      return 'red--text text--darken-1'
    },
    async hasRole(panel, op) {
      try {
        const res = await axios.get(`/admin/panel-permissions/check`, {
          params: { appPanel: panel, op }
        });
        return res.data === true;
      } catch {
        return false;
      }
    },

    async onRowClick(item) {
      const yearRoutes = {
        '2025-2026': { path: 'contract2025', route: 'contract_2025_2026-show' },
        '2024-2025': { path: 'contract2024', route: 'contract_2024_2025-show' },
        '2023-2024': { path: 'contract2023', route: 'contract_2023_2024-show' },
        '2022-2023': { path: 'contract2022', route: 'contract_2022_2023-show' },
      };

      // If we know the year, go directly
      if (item.contractYear && yearRoutes[item.contractYear]) {
        const { route } = yearRoutes[item.contractYear];
        return this.$router.push(`/${route}/${item.id}`);
      }

      // Fallback: try each year
      const token = this.$cookies.get('token');
      if (!token) return;
      localAxios.defaults.headers.common.Authorization = `Bearer ${token}`;

      for (const { path, route } of Object.values(yearRoutes)) {
        try {
          const res = await localAxios.get(`/admin/${path}/open/${item.id}`);
          if (res.status >= 200 && res.status < 300) {
            return this.$router.push(`/${route}/${item.id}`);
          }
        } catch {
          // silent, try next
        }
      }
    }



  },
  watch: {
    'studentOptions.page': 'fetchStudents',
    'studentOptions.itemsPerPage': 'fetchStudents',
  },
};
</script>

<style scoped>
.group-page {
  padding: 32px;
  background-color: #fafafa;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif;
}

/* --- Header --- */
.header-row {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
}

.header-info {
  flex: 1;
}

.header {
  display: flex;
  align-items: center;
}

.modern-back-btn {
  border: 1px solid #e0e0e0 !important;
  background: linear-gradient(135deg, #ffffff, #f9f9f9) !important;
  color: #1a1a2e !important;
  text-transform: none;
  min-width: 140px;
  height: 46px;
  transition: all 0.2s ease !important;
}
.modern-back-btn:hover {
  transform: translateY(-1px);
  border-color: #ccc !important;
}
.modern-back-btn:active {
  transform: translateY(0);
}
.back-icon {
  color: #1a1a2e !important;
  transition: transform 0.2s;
}
.modern-back-btn:hover .back-icon {
  transform: translateX(-3px);
}

.title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}
.title-accent {
  color: #1a1a2e;
  font-weight: 800;
}

/* --- Subtitle --- */
.subtitle {
  font-size: 0.8rem;
  color: #888;
  margin-top: 4px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

/* --- Loader --- */
.loader {
  text-align: center;
  color: #888;
  font-size: 1rem;
  padding: 24px 0;
}

/* --- Stats Card --- */
.stats-card {
  display: flex;
  background-color: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  overflow: hidden;
}

.stat-item {
  flex: 1;
  padding: 20px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.stat-item + .stat-item {
  border-left: 1px solid #e8e8e8;
}

.stat-label {
  font-size: 0.7rem;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
}

.stat-value {
  margin-top: 6px;
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a1a2e;
}

/* --- Teacher Card --- */
.teacher-card {
  margin-top: 16px;
}

.teacher-card-inner {
  background-color: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  padding: 18px 24px;
}

.teacher-label {
  font-size: 0.7rem;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 10px;
  font-weight: 600;
}

.teacher-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.teacher-info {
  display: flex;
  align-items: center;
}

.teacher-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a1a2e;
}

.teacher-edit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: #fafafa;
  color: #888;
  cursor: pointer;
  transition: all .15s ease;
}
.teacher-edit-btn:hover {
  background: #f0f0f0;
  color: #333;
  border-color: #ccc;
}

.teacher-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.teacher-select {
  max-width: 400px;
}

.teacher-cancel-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: #fafafa;
  color: #888;
  cursor: pointer;
  transition: all .15s ease;
}
.teacher-cancel-btn:hover {
  background: #fee;
  color: #c62828;
  border-color: #e0b0b0;
}

/* --- Students Table Styles --- */
.students-table {
  margin-top: 20px;
}

.students-card {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e8e8e8;
  box-shadow: none !important;
}

/* --- Stats Wrapper with Year Filter --- */
.stats-wrapper {
  display: flex;
  align-items: stretch;
  gap: 12px;
}

.stats-wrapper .stats-card {
  flex: 1;
}

.year-filter-top {
  display: flex;
  align-items: center;
  background-color: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  padding: 16px 20px;
  min-width: 200px;
}

.year-select-top {
  width: 100%;
}

.year-current-label {
  margin-top: 8px;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: #1a1a2e;
  letter-spacing: 0.5px;
}

.student-search {
  max-width: 200px;
}

.power-table .v-data-table__wrapper {
  overflow-x: auto;
}

.power-table th {
  background-color: #f5f5f5 !important;
  color: #555 !important;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 11px !important;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e8e8e8 !important;
}

.power-table tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

::v-deep .power-table td:first-child,
::v-deep .power-table th:first-child { padding-left: 16px !important; }
::v-deep .power-table td:last-child,
::v-deep .power-table th:last-child { padding-right: 8px !important; }

/* Student row number */
.s-row-num { font-size: 12px; font-weight: 600; color: #9ca3af; }

/* Student leaderboard rank badge */
.s-rank-badge {
  width: 22px; height: 22px; border-radius: 6px;
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 11px; flex-shrink: 0;
  background: linear-gradient(135deg, #ffd451, #f5c542);
  color: #1a1500;
  box-shadow: 0 2px 6px rgba(255,193,7,.3);
}

/* Student percent bar */
.s-pct-bar { flex: 1; height: 6px; background: rgba(0,0,0,.06); border-radius: 3px; overflow: hidden; }
.s-pct-fill { height: 100%; border-radius: 3px; transition: width .4s ease; }
.s-pct-green { background: #2e7d32; }
.s-pct-yellow { background: #f9a825; }
.s-pct-red { background: #e53935; }
.s-pct-gold { background: #2e7d32 !important; }
.s-pct-text-gold {
  color: #2e7d32 !important;
  font-weight: 800 !important;
}

/* Top 5 row highlight */
::v-deep .s-gold-row {
  background: linear-gradient(90deg, rgba(46,125,50,.08), rgba(76,175,80,.03)) !important;
  border-left: 3px solid #43a047;
}
::v-deep .s-gold-row:hover {
  background: linear-gradient(90deg, rgba(46,125,50,.14), rgba(76,175,80,.06)) !important;
}

/* ═══════════════════════════════════════
   DARK THEME OVERRIDES
   ═══════════════════════════════════════ */

.theme--dark .group-page {
  background-color: #111318;
}

.theme--dark .modern-back-btn {
  background: #1e1f2a !important;
  border-color: #2a2c3a !important;
  color: #e4e5eb !important;
}
.theme--dark .modern-back-btn:hover {
  border-color: #3a3c4a !important;
}
.theme--dark .back-icon { color: #e4e5eb !important; }

.theme--dark .title { color: #e4e5eb; }
.theme--dark .title-accent { color: #e4e5eb; }
.theme--dark .subtitle { color: #666; }
.theme--dark .loader { color: #8b8d9e; }

.theme--dark .stats-card {
  background-color: #1a1b23;
  border-color: #2a2c3a;
}
.theme--dark .stat-item + .stat-item { border-left-color: #2a2c3a; }
.theme--dark .stat-label { color: #666; }
.theme--dark .stat-value { color: #e4e5eb; }

.theme--dark .year-filter-top {
  background-color: #1a1b23;
  border-color: #2a2c3a;
}
.theme--dark .year-current-label { color: #e4e5eb; }

.theme--dark .teacher-card-inner {
  background-color: #1a1b23;
  border-color: #2a2c3a;
}
.theme--dark .teacher-label { color: #666; }
.theme--dark .teacher-name { color: #e4e5eb; }
.theme--dark .teacher-edit-btn {
  background: #22232e;
  border-color: #2a2c3a;
  color: #8b8d9e;
}
.theme--dark .teacher-edit-btn:hover {
  background: #2a2c3a;
  color: #e4e5eb;
}
.theme--dark .teacher-cancel-btn {
  background: #22232e;
  border-color: #2a2c3a;
  color: #8b8d9e;
}
.theme--dark .teacher-cancel-btn:hover {
  background: #3a1515;
  color: #ef5350;
  border-color: #5a2020;
}

.theme--dark .students-card {
  background-color: #1a1b23 !important;
  border-color: #2a2c3a !important;
}
.theme--dark .power-table th {
  background-color: #1e1f2a !important;
  color: #666 !important;
  border-bottom-color: #2a2c3a !important;
}
.theme--dark .power-table tbody tr:hover {
  background-color: rgba(255,255,255,.03);
}

/* Dark leaderboard */
.theme--dark .s-pct-bar { background: rgba(255,255,255,.08); }
.theme--dark .s-rank-badge { box-shadow: 0 2px 6px rgba(255,193,7,.2); }
.theme--dark ::v-deep .s-gold-row {
  background: linear-gradient(90deg, rgba(46,125,50,.08), rgba(76,175,80,.03)) !important;
  border-left: 3px solid rgba(67,160,71,.6);
}
.theme--dark ::v-deep .s-gold-row:hover {
  background: linear-gradient(90deg, rgba(46,125,50,.14), rgba(76,175,80,.06)) !important;
}
.theme--dark .s-pct-text-gold {
  color: #66bb6a !important;
}

/* ═══════════════════════════════════════
   MODERN INPUTS & DROPDOWNS
   ═══════════════════════════════════════ */

/* Input base */
::v-deep .v-input--outlined .v-input__slot {
  border-radius: 10px !important;
  background: #fff !important;
  min-height: 44px !important;
  transition: border-color .15s ease !important;
}
::v-deep .v-input--outlined fieldset {
  border: 1px solid #e0e0e0 !important;
}
::v-deep .v-input--outlined.v-input--is-focused fieldset {
  border-color: #00274c !important;
  border-width: 1.5px !important;
}
/* Hide floating label */
::v-deep .v-input--outlined legend { display: none !important; }
::v-deep .v-label--active { display: none !important; }
::v-deep .v-label { font-size: 13px !important; color: #9e9e9e !important; }

/* Text & selection */
::v-deep .v-text-field input { font-size: 13px !important; font-weight: 500 !important; }
::v-deep .v-select__selection { font-size: 13px !important; font-weight: 500 !important; }

/* Arrow icon — smaller, subtle */
::v-deep .v-input__append-inner {
  margin-top: 0 !important;
  align-self: center !important;
}
::v-deep .v-input__append-inner .v-icon {
  font-size: 18px !important;
  color: #bdbdbd !important;
  transition: transform .15s ease, color .15s ease !important;
}
::v-deep .v-input--is-focused .v-input__append-inner .v-icon {
  transform: rotate(180deg) !important;
  color: #00274c !important;
}

/* Clear icon */
::v-deep .v-input__icon--clear .v-icon {
  font-size: 16px !important;
  color: #bdbdbd !important;
}
::v-deep .v-input__icon--clear .v-icon:hover {
  color: #757575 !important;
}

/* Chips inside multi-select */
::v-deep .v-select__selections .v-chip {
  border-radius: 6px !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  height: 24px !important;
  background: rgba(0,0,0,.04) !important;
  color: #333 !important;
  border: none !important;
}

/* Dark inputs */
.theme--dark ::v-deep .v-input--outlined .v-input__slot {
  background: #1e1f2a !important;
}
.theme--dark ::v-deep .v-input--outlined fieldset {
  border-color: #333546 !important;
}
.theme--dark ::v-deep .v-input--outlined.v-input--is-focused fieldset {
  border-color: #ffd451 !important;
}
.theme--dark ::v-deep .v-input__append-inner .v-icon {
  color: #555 !important;
}
.theme--dark ::v-deep .v-input--is-focused .v-input__append-inner .v-icon {
  color: #ffd451 !important;
}
.theme--dark ::v-deep .v-input__icon--clear .v-icon {
  color: #555 !important;
}
.theme--dark ::v-deep .v-select__selections .v-chip {
  background: rgba(255,255,255,.06) !important;
  color: #d5d6de !important;
}
.theme--dark ::v-deep .v-label { color: #666 !important; }
.theme--dark ::v-deep .v-text-field input { color: #e4e5eb !important; }
.theme--dark ::v-deep .v-select__selection { color: #e4e5eb !important; }

/* ── Mobile / Tablet Responsive ── */
@media (max-width: 960px) {
  .header-row { flex-wrap: wrap; gap: 12px; }
  .header-info { flex: 1 1 100%; }
  .stats-wrapper { flex-direction: column; }
  .stats-card { flex-direction: row; }
  .teacher-card { margin-top: 0; }
}

@media (max-width: 600px) {
  .group-page { padding: 0 8px !important; }
  .header-row { gap: 8px; margin-bottom: 12px; }
  .title { font-size: 1.1rem !important; }
  .subtitle { font-size: 0.8rem !important; }
  .stats-card { flex-direction: column; }
  .stat-item { padding: 12px 16px; }
  .stat-item + .stat-item { border-left: none; border-top: 1px solid #e8e8e8; }
  .stat-value { font-size: 1.8rem !important; }
  .year-filter-top { min-width: 100%; }
  .teacher-card-inner { flex-wrap: wrap; gap: 8px; }
  ::v-deep .v-data-table { overflow-x: auto; }
  ::v-deep .v-data-table table { min-width: 700px; }
}
</style>